/**
 * Financeiro > Repasses antigos (spec 054, 4; Vitor, 15/10): os repasses que vieram do sistema antigo numa planilha,
 * pra acertar o plano, a etapa, o inicio de cada etapa e o valor sem avisar o cliente. Nome, CPF, e-mail e subconta
 * ficam fixos (filtro e pra nao confundir); a subconta muda no plano, pelo Gerenciar plano.
 *
 * Cada linha guarda o rascunho dela e salva sozinha, numa transacao so no backend. Etapa e inicios sao do PLANO: dois
 * repasses do mesmo plano mostram a mesma etapa, e salvar um muda a do outro.
 */
import { Aviso, Botao, CabecalhoDePagina, Campo, Carregando, DinheiroDaPlanilha, EntradaDaPlanilha, ErroApi, LinkInterno, Pilha, Planilha, SelecaoDaPlanilha, Vazio, formatarDia, formatarReais, type EstadoDaLinha } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { erroDe, useApi } from "../api";
import { usePagina } from "../pagina";
import { ForaDaPagina } from "./ForaDaPagina";

type Etapa = "TESTE" | "SIMULADOR" | "MESA_REAL";
type PlanoDaLinha = { id: string; plano: string; status: string; comprada_em: string; subconta: string | null; etapa: Etapa | null; etapas_da_familia: { codigo: Etapa; rotulo: string }[]; inicios: Partial<Record<Etapa, string | null>> };
type Linha = { id: string; pago_em: string; valor_liquido_centavos: number; valor_bruto_centavos: number | null; descontos_centavos: number | null; matricula_id: string | null; alterado_em: string | null; pessoa: { id: string; nome: string; cpf: string; email: string | null }; planos: PlanoDaLinha[] };
/** O que mudou na linha e ainda nao foi salvo. `plano` "" = sem plano. */
type Rascunho = { plano?: string; etapa?: Etapa; inicios?: Partial<Record<Etapa, string>>; liquido?: number; bruto?: number | null; descontos?: number | null };

const ETAPAS: { codigo: Etapa; rotulo: string }[] = [{ codigo: "TESTE", rotulo: "Teste" }, { codigo: "SIMULADOR", rotulo: "Simulador" }, { codigo: "MESA_REAL", rotulo: "Mesa Real" }];
const PEDE_DECISAO = "DECIDIR_MENSALIDADE";

export function PaginaRepassesAntigos() {
  const { ler, mudar } = useApi();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["repasses", "antigos"], queryFn: () => ler<{ itens: Linha[] }>("/v1/admin/repasses/antigos") });
  const [rascunhos, setRascunhos] = useState<Record<string, Rascunho>>({});
  const [erros, setErros] = useState<Record<string, { texto: string; gerenciar?: string }>>({});
  const [salvando, setSalvando] = useState<string | null>(null);
  const [feito, setFeito] = useState<string>();
  const [busca, setBusca] = useState("");
  const pag = usePagina("pagina", [busca]);

  const r = (l: Linha) => rascunhos[l.id] ?? {};
  const plano = (l: Linha) => r(l).plano ?? l.matricula_id ?? "";
  const planoDa = (l: Linha) => l.planos.find((p) => p.id === plano(l));
  const etapa = (l: Linha) => r(l).etapa ?? planoDa(l)?.etapa ?? null;
  const inicio = (l: Linha, x: Etapa) => r(l).inicios?.[x] ?? planoDa(l)?.inicios[x] ?? "";
  /** As etapas que o plano ja percorreu, ate a escolhida: so elas tem inicio. */
  const percorreu = (l: Linha, x: Etapa) => {
    const ordem = planoDa(l)?.etapas_da_familia.map((e) => e.codigo) ?? [];
    const e = etapa(l);
    return Boolean(e) && ordem.includes(x) && ordem.indexOf(x) <= ordem.indexOf(e!);
  };
  const liquido = (l: Linha) => r(l).liquido ?? l.valor_liquido_centavos;
  const bruto = (l: Linha) => (r(l).bruto !== undefined ? r(l).bruto! : l.valor_bruto_centavos);
  const descontos = (l: Linha) => (r(l).descontos !== undefined ? r(l).descontos! : l.descontos_centavos);

  const mexer = (l: Linha, p: Rascunho) => {
    setFeito(undefined);
    setErros(({ [l.id]: _, ...e }) => e);
    setRascunhos((t) => ({ ...t, [l.id]: { ...t[l.id], ...p } }));
  };
  // Trocar o plano zera a etapa e os inicios digitados: eram do plano de antes.
  const trocarPlano = (l: Linha, p: string) => setRascunhos((t) => { const { etapa: _e, inicios: _i, ...resto } = t[l.id] ?? {}; return { ...t, [l.id]: { ...resto, plano: p } }; });
  const desfazer = (l: Linha) => { setRascunhos(({ [l.id]: _, ...t }) => t); setErros(({ [l.id]: _, ...e }) => e); };

  /** So o que mudou vai pro backend. */
  const corpo = (l: Linha) => {
    const d = r(l);
    const mudouValor = d.liquido !== undefined || d.bruto !== undefined || d.descontos !== undefined;
    const inicios = Object.fromEntries(Object.entries(d.inicios ?? {}).filter(([x, v]) => v && v !== planoDa(l)?.inicios[x as Etapa]));
    return {
      ...(d.plano !== undefined && d.plano !== (l.matricula_id ?? "") ? { matricula_id: d.plano || null } : {}),
      ...(mudouValor ? { valor_liquido_centavos: liquido(l), valor_bruto_centavos: bruto(l), descontos_centavos: bruto(l) ? (descontos(l) ?? 0) : null } : {}),
      ...(d.etapa && d.etapa !== planoDa(l)?.etapa ? { etapa: d.etapa } : {}),
      ...(Object.keys(inicios).length ? { inicios } : {}),
    };
  };
  const salvar = async (l: Linha) => {
    setSalvando(l.id);
    try {
      await mudar(`/v1/admin/repasses/antigos/${l.id}`, corpo(l));
      await qc.invalidateQueries({ queryKey: ["repasses"] });
      desfazer(l);
      setFeito(`Repasse de ${l.pessoa.nome} salvo. O cliente não foi avisado.`);
    } catch (e) {
      const gerenciar = e instanceof ErroApi && e.corpo?.codigo === PEDE_DECISAO && plano(l) ? plano(l) : undefined;
      setErros((t) => ({ ...t, [l.id]: { texto: gerenciar ? "Essa mudança faz o plano passar a pagar mensalidade. Faça pelo Gerenciar plano, que pergunta o que fazer com a primeira." : erroDe(e), gerenciar } }));
    } finally {
      setSalvando(null);
    }
  };

  const termo = busca.trim().toLowerCase();
  const digitos = termo.replace(/\D/g, "");
  const linhas = (q.data?.itens ?? []).filter((l) => !termo
    || l.pessoa.nome.toLowerCase().includes(termo)
    || (l.pessoa.email ?? "").toLowerCase().includes(termo)
    || (digitos.length >= 3 && (l.pessoa.cpf.replace(/\D/g, "").includes(digitos) || l.planos.some((p) => (p.subconta ?? "").includes(digitos)))));
  const estado = (l: Linha): EstadoDaLinha => (erros[l.id] ? "erro" : rascunhos[l.id] ? "mudou" : undefined);
  const mudou = (l: Linha, k: keyof Rascunho) => r(l)[k] !== undefined;

  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Repasses antigos" descricao="Os repasses que vieram do sistema antigo. Acerte o plano, a etapa, o início de cada etapa e o valor direto na planilha: o cliente não é avisado e a trilha guarda o antes e o depois. A subconta muda no plano, pelo Gerenciar plano." />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      <div style={{ maxWidth: 360 }}><Campo rotulo="Buscar" valor={busca} aoMudar={setBusca} placeholder="Nome, CPF, e-mail ou subconta" /></div>
      {q.isLoading ? <Carregando compacto texto="Buscando os repasses antigos" /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : (
        <>
          <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{linhas.length} de {q.data!.itens.length} {q.data!.itens.length === 1 ? "repasse" : "repasses"}. {q.data!.itens.filter((l) => !l.matricula_id).length} sem plano.</span>
          <ForaDaPagina linhas={linhas} alteradas={(l) => Boolean(rascunhos[l.id])} nome={(l) => l.pessoa.nome} pagina={pag.pagina} aoIr={pag.aoMudar} />
          <Planilha
            rotulo="Repasses antigos"
            linhas={linhas}
            paginacao={pag}
            chave={(l) => l.id}
            estado={estado}
            vazio={<Vazio titulo={termo ? "Ninguém com essa busca" : "Nenhum repasse antigo"}>{termo ? "Confira o nome, o CPF, o e-mail ou a subconta." : "Os repasses que vieram do sistema antigo aparecem aqui."}</Vazio>}
            colunas={[
              { chave: "nome", rotulo: "Nome", fixa: true, celula: (l) => <>{l.matricula_id ? <LinkInterno className="mtc-link" href={`/matriculas/${l.matricula_id}`}><strong>{l.pessoa.nome}</strong></LinkInterno> : <strong>{l.pessoa.nome}</strong>}<span className="mtc-planilha__apoio">PIX de {formatarDia(l.pago_em)}</span></> },
              { chave: "cpf", rotulo: "CPF", celula: (l) => <span className="mtc-mono">{l.pessoa.cpf}</span> },
              { chave: "email", rotulo: "E-mail", celula: (l) => l.pessoa.email ?? <span className="mtc-texto-2">-</span> },
              { chave: "subconta", rotulo: "Subconta", celula: (l) => <span className="mtc-mono">{planoDa(l)?.subconta ?? "-"}</span> },
              {
                chave: "plano", rotulo: "Plano", celula: (l) => (
                  <SelecaoDaPlanilha
                    rotulo={`Plano do repasse de ${l.pessoa.nome}`}
                    valor={plano(l)}
                    mudou={mudou(l, "plano")}
                    aoMudar={(p) => { setFeito(undefined); setErros(({ [l.id]: _, ...e }) => e); trocarPlano(l, p); }}
                    opcoes={[{ valor: "", rotulo: l.planos.length ? "Sem plano" : "Sem plano (o cliente não tem plano aqui)" }, ...l.planos.map((p) => ({ valor: p.id, rotulo: `${p.plano}, ${formatarDia(p.comprada_em)}${p.status === "ENCERRADA" ? " (encerrado)" : ""}` }))]}
                  />
                ),
              },
              {
                chave: "etapa", rotulo: "Etapa", celula: (l) => {
                  const p = planoDa(l);
                  if (!p) return <span className="mtc-texto-2">-</span>;
                  if (!p.etapa) return <span className="mtc-planilha__apoio">Ainda não liberado</span>;
                  return <SelecaoDaPlanilha rotulo={`Etapa de ${l.pessoa.nome}`} valor={etapa(l) ?? ""} mudou={mudou(l, "etapa")} aoMudar={(e) => mexer(l, { etapa: e as Etapa })} opcoes={p.etapas_da_familia.map((e) => ({ valor: e.codigo, rotulo: e.rotulo }))} />;
                },
              },
              ...ETAPAS.map((x) => ({
                chave: `inicio-${x.codigo}`,
                rotulo: `Início ${x.rotulo}`,
                celula: (l: Linha) => percorreu(l, x.codigo)
                  ? <EntradaDaPlanilha rotulo={`Início de ${x.rotulo} de ${l.pessoa.nome}`} type="date" mono valor={inicio(l, x.codigo)} mudou={r(l).inicios?.[x.codigo] !== undefined} aoMudar={(v) => mexer(l, { inicios: { ...r(l).inicios, [x.codigo]: v } })} />
                  : <span className="mtc-texto-2">-</span>,
              })),
              { chave: "bruto", rotulo: "Bruto", alinhar: "direita" as const, celula: (l) => <DinheiroDaPlanilha rotulo={`Valor bruto de ${l.pessoa.nome}`} centavos={bruto(l)} mudou={mudou(l, "bruto")} aoMudar={(c) => mexer(l, c ? { bruto: c, descontos: Math.max(0, c - liquido(l)) } : { bruto: null, descontos: null })} /> },
              { chave: "descontos", rotulo: "Descontos", alinhar: "direita" as const, celula: (l) => <DinheiroDaPlanilha rotulo={`Descontos de ${l.pessoa.nome}`} centavos={descontos(l)} disabled={!bruto(l)} mudou={mudou(l, "descontos")} aoMudar={(c) => mexer(l, { descontos: c ?? 0, liquido: Math.max(0, (bruto(l) ?? 0) - (c ?? 0)) })} /> },
              { chave: "liquido", rotulo: "Líquido", alinhar: "direita" as const, celula: (l) => <DinheiroDaPlanilha rotulo={`Valor líquido de ${l.pessoa.nome}`} centavos={liquido(l)} mudou={mudou(l, "liquido")} aoMudar={(c) => mexer(l, { liquido: c ?? 0, ...(bruto(l) ? { descontos: Math.max(0, bruto(l)! - (c ?? 0)) } : {}) })} /> },
              {
                chave: "acoes", rotulo: "", celula: (l) => (
                  <div style={{ display: "grid", gap: "var(--e-1)", minWidth: 180 }}>
                    {rascunhos[l.id] && (
                      <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
                        <Botao tamanho="pequeno" disabled={liquido(l) < 1 || salvando !== null} carregando={salvando === l.id} onClick={() => void salvar(l)}>Salvar</Botao>
                        <Botao tamanho="pequeno" variante="fantasma" disabled={salvando === l.id} onClick={() => desfazer(l)}>Desfazer</Botao>
                      </div>
                    )}
                    {erros[l.id] && (
                      <span className="mtc-planilha__apoio" role="alert" style={{ color: "var(--erro)", maxWidth: 280 }}>
                        {erros[l.id]!.texto}
                        {erros[l.id]!.gerenciar && <> <LinkInterno className="mtc-link" href={`/matriculas/${erros[l.id]!.gerenciar}`}>Abrir o plano</LinkInterno></>}
                      </span>
                    )}
                    {!rascunhos[l.id] && l.alterado_em && <span className="mtc-planilha__apoio">Corrigido aqui</span>}
                  </div>
                ),
              },
            ]}
          />
          <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Total dos repasses antigos: <strong className="mtc-mono">{formatarReais(q.data!.itens.reduce((s, l) => s + l.valor_liquido_centavos, 0))}</strong></span>
        </>
      )}
    </Pilha>
  );
}
